import React, { useEffect, useRef, useState } from 'react';
import { Mic, MicOff, Send, X, Plus } from 'lucide-react';
import { usePortalStore } from '../../store';
import type { LeanCanvasBlock, PostIt } from '../../types';

// ── Lean Canvas block metadata ────────────────────────────────────────────────

interface BlockDef {
  id: LeanCanvasBlock;
  label: string;
  hint: string;
  gridArea: string;
}

const BLOCKS: BlockDef[] = [
  { id: 'problem', label: 'Problem', hint: 'Top 3 problems', gridArea: 'problem' },
  { id: 'solution', label: 'Solution', hint: 'Top 3 features', gridArea: 'solution' },
  { id: 'unique_value_proposition', label: 'Unique Value Proposition', hint: 'Clear, compelling message', gridArea: 'uvp' },
  { id: 'unfair_advantage', label: 'Unfair Advantage', hint: 'Cannot be easily copied', gridArea: 'unfair' },
  { id: 'customer_segments', label: 'Customer Segments', hint: 'Target customers', gridArea: 'customers' },
  { id: 'existing_alternatives', label: 'Existing Alternatives', hint: 'How they solve today', gridArea: 'alternatives' },
  { id: 'key_metrics', label: 'Key Metrics', hint: 'Key activities to measure', gridArea: 'metrics' },
  { id: 'channels', label: 'Channels', hint: 'Path to customers', gridArea: 'channels' },
  { id: 'cost_structure', label: 'Cost Structure', hint: 'Customer acquisition, distribution, hosting', gridArea: 'costs' },
  { id: 'revenue_streams', label: 'Revenue Streams', hint: 'Revenue model, lifetime value, gross margin', gridArea: 'revenue' },
];

// ── Post-it component ─────────────────────────────────────────────────────────

interface PostItCardProps {
  postit: PostIt;
  onRemove: (id: string) => void;
}

const PostItCard: React.FC<PostItCardProps> = ({ postit, onRemove }) => {
  const isCofounder = postit.author === 'cofounder';

  return (
    <div
      className={`
        group relative p-2 rounded text-xs leading-snug shadow-sm
        transition-all duration-500 ease-out
        ${postit.isAnimating ? 'opacity-0 scale-90 translate-y-1' : 'opacity-100 scale-100 translate-y-0'}
      `}
      style={
        isCofounder
          ? {
              backgroundColor: 'var(--active-overlay)',
              border: '1px solid var(--accent-teal)',
              color: 'var(--text-primary)',
            }
          : {
              backgroundColor: 'var(--hover-overlay)',
              border: '1px solid var(--accent-amber)',
              color: 'var(--text-primary)',
            }
      }
    >
      {isCofounder && (
        <span
          className="block font-semibold mb-1 uppercase tracking-wide"
          style={{ fontSize: '9px', color: 'var(--accent-teal)' }}
        >
          co-founder
        </span>
      )}
      <p>{postit.content}</p>
      <button
        onClick={() => onRemove(postit.id)}
        className="absolute top-1 right-1 hidden group-hover:flex items-center justify-center w-4 h-4 rounded-full"
        style={{ backgroundColor: 'var(--bg-elevated)', color: 'var(--text-secondary)' }}
        aria-label="Remove post-it"
      >
        <X className="w-2.5 h-2.5" />
      </button>
    </div>
  );
};

// ── Add post-it inline form ───────────────────────────────────────────────────

interface AddPostItProps {
  block: LeanCanvasBlock;
  onAdd: (block: LeanCanvasBlock, content: string) => void;
  onCancel: () => void;
}

const AddPostItForm: React.FC<AddPostItProps> = ({ block, onAdd, onCancel }) => {
  const [value, setValue] = useState('');
  const ref = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    ref.current?.focus();
  }, []);

  const handleSubmit = () => {
    const trimmed = value.trim();
    if (trimmed) onAdd(block, trimmed);
    else onCancel();
  };

  return (
    <div className="mt-1">
      <textarea
        ref={ref}
        value={value}
        onChange={e => setValue(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSubmit(); }
          if (e.key === 'Escape') onCancel();
        }}
        placeholder="Add a note..."
        rows={2}
        className="w-full p-1.5 text-xs rounded resize-none focus:outline-none"
        style={{
          backgroundColor: 'var(--bg-elevated)',
          border: '1px solid var(--accent-amber)',
          color: 'var(--text-primary)',
          fontFamily: 'var(--font-ui)',
        }}
      />
      <div className="flex gap-1 mt-1">
        <button
          onClick={handleSubmit}
          className="px-2 py-0.5 rounded text-white"
          style={{ fontSize: '10px', backgroundColor: 'var(--accent-amber)', color: 'var(--bg-primary)' }}
        >
          Add
        </button>
        <button
          onClick={onCancel}
          className="px-2 py-0.5 rounded"
          style={{ fontSize: '10px', backgroundColor: 'var(--bg-depth-3)', color: 'var(--text-secondary)' }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

// ── Canvas block cell ─────────────────────────────────────────────────────────

interface BlockCellProps {
  block: BlockDef;
  postits: PostIt[];
  onAddPostIt: (block: LeanCanvasBlock, content: string) => void;
  onRemovePostIt: (id: string) => void;
}

const BlockCell: React.FC<BlockCellProps> = ({ block, postits, onAddPostIt, onRemovePostIt }) => {
  const [adding, setAdding] = useState(false);

  return (
    <div
      className="flex flex-col p-2 h-full overflow-hidden"
      style={{
        border: '1px solid var(--border-subtle)',
        backgroundColor: 'var(--bg-secondary)',
      }}
    >
      <div className="flex items-start justify-between mb-1">
        <div>
          <h3
            className="font-bold uppercase tracking-wider leading-tight"
            style={{ fontSize: '10px', color: 'var(--text-secondary)', fontFamily: 'var(--font-ui)' }}
          >
            {block.label}
          </h3>
          <p style={{ fontSize: '9px', color: 'var(--text-tertiary)', marginTop: '0.125rem' }}>
            {block.hint}
          </p>
        </div>
        {!adding && (
          <button
            onClick={() => setAdding(true)}
            className="flex-shrink-0 ml-1 p-0.5 transition-colors"
            style={{ color: 'var(--text-tertiary)' }}
            title="Add post-it"
          >
            <Plus className="w-3 h-3" />
          </button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto space-y-1 min-h-0">
        {postits.map(p => (
          <PostItCard key={p.id} postit={p} onRemove={onRemovePostIt} />
        ))}
        {adding && (
          <AddPostItForm
            block={block.id}
            onAdd={(b, c) => { onAddPostIt(b, c); setAdding(false); }}
            onCancel={() => setAdding(false)}
          />
        )}
      </div>
    </div>
  );
};

// ── Chat column ───────────────────────────────────────────────────────────────

interface ChatColumnProps {
  onSend: (msg: string) => void;
}

const ChatColumn: React.FC<ChatColumnProps> = ({ onSend }) => {
  const { canvasChat, canvasChatLoading } = usePortalStore();
  const [input, setInput] = useState('');
  const [listening, setListening] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const recognitionRef = useRef<SpeechRecognition | null>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [canvasChat, canvasChatLoading]);

  const handleSend = () => {
    const trimmed = input.trim();
    if (!trimmed) return;
    onSend(trimmed);
    setInput('');
  };

  const toggleVoice = () => {
    const SpeechRecognition = window.SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) return;

    if (listening) {
      recognitionRef.current?.stop();
      setListening(false);
      return;
    }

    const rec = new SpeechRecognition();
    rec.continuous = false;
    rec.interimResults = false;
    rec.lang = 'en-US';
    rec.onresult = (e: SpeechRecognitionEvent) => {
      const transcript = e.results[0][0].transcript;
      setInput(prev => (prev ? prev + ' ' + transcript : transcript));
      setListening(false);
    };
    rec.onerror = () => setListening(false);
    rec.onend = () => setListening(false);
    rec.start();
    recognitionRef.current = rec;
    setListening(true);
  };

  const isEmpty = canvasChat.length === 0;

  return (
    <div
      className="flex flex-col h-full"
      style={{
        backgroundColor: 'var(--bg-panel)',
        borderLeft: '1px solid var(--border-default)',
      }}
    >
      {/* Header */}
      <div
        className="flex-shrink-0 px-3 py-2.5"
        style={{ borderBottom: '1px solid var(--border-subtle)' }}
      >
        <p
          className="font-semibold"
          style={{ fontSize: '0.75rem', color: 'var(--text-primary)', fontFamily: 'var(--font-ui)' }}
        >
          Co-founder
        </p>
        <p style={{ fontSize: '10px', color: 'var(--text-tertiary)', marginTop: '0.125rem', fontFamily: 'var(--font-ui)' }}>
          Strategic canvas session
        </p>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-3 py-3 space-y-4 min-h-0">
        {isEmpty && !canvasChatLoading && (
          <p
            className="leading-relaxed"
            style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)', fontFamily: 'var(--font-ui)' }}
          >
            Tell me about your business idea. What problem are you solving, and for whom?
          </p>
        )}
        {canvasChat.map(msg => (
          <div key={msg.id} className="space-y-0.5">
            <p
              className="font-medium uppercase tracking-wide"
              style={{
                fontSize: '10px',
                color: msg.role === 'founder' ? 'var(--accent-amber)' : 'var(--accent-teal)',
                fontFamily: 'var(--font-ui)',
              }}
            >
              {msg.role === 'founder' ? 'You' : 'Co-founder'}
            </p>
            <p
              className="leading-relaxed"
              style={{ fontSize: '0.75rem', color: 'var(--text-primary)', fontFamily: 'var(--font-ui)' }}
            >
              {msg.content}
            </p>
          </div>
        ))}
        {canvasChatLoading && (
          <div className="space-y-0.5">
            <p
              className="font-medium uppercase tracking-wide"
              style={{ fontSize: '10px', color: 'var(--accent-teal)', fontFamily: 'var(--font-ui)' }}
            >
              Co-founder
            </p>
            <div className="flex gap-1 items-center h-4">
              <span className="w-1.5 h-1.5 rounded-full animate-bounce" style={{ backgroundColor: 'var(--accent-teal)', animationDelay: '0ms' }} />
              <span className="w-1.5 h-1.5 rounded-full animate-bounce" style={{ backgroundColor: 'var(--accent-teal)', animationDelay: '120ms' }} />
              <span className="w-1.5 h-1.5 rounded-full animate-bounce" style={{ backgroundColor: 'var(--accent-teal)', animationDelay: '240ms' }} />
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div
        className="flex-shrink-0 px-3 py-3 space-y-2"
        style={{ borderTop: '1px solid var(--border-subtle)' }}
      >
        <textarea
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
          }}
          placeholder="What's the business about?"
          rows={3}
          className="w-full p-2 text-xs rounded resize-none focus:outline-none"
          style={{
            backgroundColor: 'var(--bg-elevated)',
            border: '1px solid var(--border-default)',
            color: 'var(--text-primary)',
            fontFamily: 'var(--font-ui)',
          }}
        />
        <div className="flex gap-2">
          <button
            onClick={toggleVoice}
            title={listening ? 'Stop listening' : 'Voice input'}
            className="p-1.5 rounded transition-colors"
            style={
              listening
                ? { backgroundColor: '#E05050', color: 'white' }
                : { backgroundColor: 'var(--bg-elevated)', color: 'var(--text-secondary)' }
            }
          >
            {listening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
          </button>
          <button
            onClick={handleSend}
            disabled={!input.trim() || canvasChatLoading}
            className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            style={{
              backgroundColor: 'var(--accent-teal)',
              color: 'var(--bg-primary)',
              fontSize: '0.75rem',
              fontFamily: 'var(--font-ui)',
            }}
          >
            <Send className="w-3.5 h-3.5" />
            Send
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Main Canvas View ──────────────────────────────────────────────────────────

export const CanvasView: React.FC = () => {
  const { canvasPostIts, loadCanvas, loadCanvasChat, addFounderPostIt, removePostIt, sendCanvasMessage } = usePortalStore();

  useEffect(() => {
    loadCanvas();
    loadCanvasChat();
  }, []);

  const postitsForBlock = (block: LeanCanvasBlock) =>
    canvasPostIts.filter(p => p.block === block);

  return (
    <div className="flex h-full w-full overflow-hidden">
      {/* Canvas stage — ~80% */}
      <div className="flex-1 overflow-auto p-3 min-w-0">
        <div className="mb-2 flex items-baseline gap-3">
          <h2
            className="font-bold uppercase tracking-wide"
            style={{ fontSize: '0.875rem', color: 'var(--text-primary)', fontFamily: 'var(--font-ui)' }}
          >
            Lean Canvas
          </h2>
          <span style={{ fontSize: '10px', color: 'var(--text-tertiary)', fontFamily: 'var(--font-ui)' }}>
            Strategic workspace — slow-changing by design
          </span>
        </div>

        {/* Lean Canvas grid */}
        <div
          className="grid h-[calc(100vh-9rem)] gap-px rounded-lg overflow-hidden"
          style={{
            gridTemplateColumns: 'repeat(5, 1fr)',
            gridTemplateRows: '1fr 1fr 0.6fr',
            gridTemplateAreas: `
              "problem   solution  uvp  unfair  customers"
              "alternatives metrics uvp  channels customers"
              "costs      costs    revenue revenue  revenue"
            `,
            backgroundColor: 'var(--border-subtle)',
          }}
        >
          {BLOCKS.map(block => (
            <div key={block.id} style={{ gridArea: block.gridArea }}>
              <BlockCell
                block={block}
                postits={postitsForBlock(block.id)}
                onAddPostIt={addFounderPostIt}
                onRemovePostIt={removePostIt}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Chat column — ~20% */}
      <div className="w-64 flex-shrink-0">
        <ChatColumn onSend={sendCanvasMessage} />
      </div>
    </div>
  );
};
